import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { doEventCheckin, getCTBadges } from '../redux/data/dataActions.js';
import { setSelectedBadge, setSelectedBadgeModalActive, setTabSelected } from "../redux/data/dataSlice.js";
import Config from "../config/memberhubContents.json";
import LoadingGif from "../assets/images/loading-gif-2.gif";
import { setupBadgeMetaData, setupBaseMetaData } from "../utils/metaData.js";
import { injectPageScripts } from "../utils/loadScripts.js";
import { confetti } from "../utils/confetti.js";
import BadgeSection from "./badges/badgeSection.jsx";
import BadgeModal from "./badges/badgeModal.jsx";
import TabSectionHeader from "./tabSectionHeader.jsx";
import "../assets/css/header.css";
import "../assets/css/badges.css";

function BadgesCT() {
    const dispatch = useDispatch();
    const queryParams = new URLSearchParams(window.location.search);
    const eci = queryParams.get("eci");
    const params = useParams();
    const badgesLoading = useSelector(state => state.data.badgesLoading);
    const ctBadgeCounts = useSelector(state => state.data.ctBadgeCounts);
    const ctBadgeSections = useSelector(state => state.data.ctBadgeSections);
    const userType = useSelector(state => state.data.userType);
    const needsCTBadges = useSelector(state => state.data.needsCTBadges);
    const selectedBadge = useSelector(state => state.data.selectedBadge);
    const selectedBadgeModalActive = useSelector(state => state.data.selectedBadgeModalActive);
    const eventCheckin = useSelector(state => state.data.eventCheckin);

    useEffect(() => {
        console.log("query eci:", eci);

        if (eci && eci.length > 5) {
            let activityId = null;
            switch (eci) {
                case "81c12f3e-1851-4bd8-bdb2-63aa4119310d":
                    activityId = "410171757";
                    break;

                case "857cbff1-db34-4508-865c-842e0a057795":
                    activityId = "410171735";
                    break;

                case "99377901-2d39-47e3-887f-e7145ad26b36":
                    activityId = "410171758";
                    break;

                case "40a20dcf-8ebb-4ae7-a78a-1e5587492ea9":
                    activityId = "410264603"
                    break;

                default:
                    break;
            }

            if (activityId) {
                dispatch(doEventCheckin(activityId));
            }
        }
    }, [dispatch, eci]);

    useEffect(() => {
        console.log("eventCheckin:", eventCheckin);

        if (eventCheckin) {
            const url = new URL(window.location.href);
            url.searchParams.delete("eci");
            window.location.href = url.href;
        }

    }, [eventCheckin]);

    useEffect(() => {
        const clickedOnBadge = window.location.search.includes("cl=1"); // as opposed to deep link to badge
        if (!clickedOnBadge && !selectedBadgeModalActive) {
            window.digitalData.page.pageInfo.pageName = "nba:membership-hub:badges";
            window.digitalData.page.pageInfo.siteSection = "nba:membership-hub";
            window.digitalData.page.content = { categoryTitle: "Badges", categoryType: "membership-hub" };
            window.digitalData.site = { propertyName: "nba:web", league: "nba" };
            if (window.digitalData.user) {
                window.digitalData.user.userState = "authenticated";
                window.digitalData.user.userType = userType;
            }

            console.log("Badges: analytics added");
        }
    }, [selectedBadgeModalActive, userType]);

    useEffect(() => {
        console.log("Badges: useEffect[selectedBadgeModalActive]");

        const bodyElement = document.getElementById("body");
        if (bodyElement) {
            if (selectedBadgeModalActive) {
                if (!bodyElement.className.includes("modal-noscroll")) {
                    bodyElement.className += " modal-noscroll";
                }
            } else {
                if (bodyElement.className.includes("modal-noscroll")) {
                    bodyElement.className = bodyElement.className.replace(" modal-noscroll", "");
                }
            }
        }

    }, [selectedBadgeModalActive]);

    useEffect(() => {
        const openModal = badge => {
            dispatch(setSelectedBadge(badge));
            dispatch(setSelectedBadgeModalActive(true));
            setupBadgeMetaData(badge);

            const confettiShown = JSON.parse(localStorage.getItem('confetti')) ?? [];
            console.log("confettiShown:", confettiShown);

            if (badge.is_new && !confettiShown.includes(badge.id)) {
                confetti.start();

                confettiShown.push(badge.id);
                localStorage.setItem('confetti', JSON.stringify(confettiShown));

                setTimeout(function () {
                    confetti.stop();
                }, 5000);
            }
        };

        const findBadgeById = id => {
            if (id && ctBadgeSections) {
                let foundBadge;
                for (let section in ctBadgeSections) {
                    if (ctBadgeSections[section].badges && ctBadgeSections[section].badges.length > 0) {
                        foundBadge = ctBadgeSections[section].badges.find(badge => badge.id === +id);
                        if (foundBadge) return foundBadge;
                    }
                }
            }
            return null;
        };

        if (params && params.badgeId) {
            const badge = findBadgeById(params.badgeId);

            if (badge) {
                openModal(badge);
            }
        }

        setupBaseMetaData();
        injectPageScripts();
    }, [params, params.badgeId, ctBadgeSections, dispatch]);

    useEffect(() => {
        console.log("Badges: useEffect[dispatch]");
        dispatch(setTabSelected("badges"));
        console.log("tabSelected: ct-badges");
    }, [dispatch]);

    useEffect(() => {
        console.log("Badges: useEffect[dispatch, badgesLoading, needsBadges]");

        if (!badgesLoading && needsCTBadges) {
            dispatch(getCTBadges("badges"));
        }
    }, [dispatch, badgesLoading, needsCTBadges]);

    return (<>
        <div className="badge-page-container">
            <TabSectionHeader title={Config.badgesHeading} subtitle={Config.evergreenBadgesDescription}
                count={ctBadgeCounts.earned} countText="earned" />
            <div className="title-bar"></div>
            <div className="badge-swimlane-container">
                {!ctBadgeSections || ctBadgeSections.length === 0 ?
                    <div className="earned-badges-loading-gif" style={{ alignSelf: "center" }}>
                        <img src={LoadingGif} alt="" />
                    </div>
                    :
                    <div>
                        {/* Section 1: My Fan Section */}
                        {ctBadgeSections["1"] && ctBadgeSections["1"].badges && ctBadgeSections["1"].badges.length > 0 && <BadgeSection section={ctBadgeSections["1"]} hideCount={true} />}
                        {/* Section 8: NBA ID Challenges */}
                        {ctBadgeSections["8"] && ctBadgeSections["8"].badges && ctBadgeSections["8"].badges.length > 0 && <BadgeSection section={ctBadgeSections["8"]} hideCount={true} />}
                        {/* Section 3: Watch Milestones */}
                        {ctBadgeSections["3"] && ctBadgeSections["3"].badges && ctBadgeSections["3"].badges.length > 0 && <BadgeSection section={ctBadgeSections["3"]} hideCount={true} />}
                        {/* Section 2: Vote Milestones */}
                        {ctBadgeSections["2"] && ctBadgeSections["2"].badges && ctBadgeSections["2"].badges.length > 0 && <BadgeSection section={ctBadgeSections["2"]} hideCount={true} />}
                        {/* Section 3: Play Milestones */}
                        {ctBadgeSections["4"] && ctBadgeSections["4"].badges && ctBadgeSections["4"].badges.length > 0 && <BadgeSection section={ctBadgeSections["4"]} hideCount={true} />}
                        {/* Section 5: Pick 'em */}
                        {ctBadgeSections["5"] && ctBadgeSections["5"].badges && ctBadgeSections["5"].badges.length > 0 && <BadgeSection section={ctBadgeSections["5"]} hideCount={true} />}
                        {/* Section 6: NBA Events */}
                        {ctBadgeSections["6"] && ctBadgeSections["6"].badges && ctBadgeSections["6"].badges.length > 0 && <BadgeSection section={ctBadgeSections["6"]} hideCount={true} />}
                        {/* Section 7: Reigning Champs */}
                        {ctBadgeSections["7"] && ctBadgeSections["7"].badges && ctBadgeSections["7"].badges.length > 0 && <BadgeSection section={ctBadgeSections["7"]} hideCount={true} />}
                    </div>
                }
            </div>
        </div>
        {selectedBadgeModalActive && selectedBadge && <BadgeModal />}
    </>);
}

export default BadgesCT;
